import { Button } from '@/components/ui/button.jsx'
import { Icon } from '@iconify/react'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion.jsx'
import { motion } from 'framer-motion'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useResearchContext } from '@/context-state/ResearchContext.jsx'
import { AnalyticsService } from '@/services/analytics.js'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog.jsx'
import { Input } from '@/components/ui/input.jsx'
import { useState } from 'react'
import { toast } from 'sonner'
import { handleError } from '@/services/errorHandler.js'
import * as API from '@/services/api.js'
import { toggleLoader } from '@/hooks/useDashboardLoader.jsx'
import { TypographySmallMuted } from '@/components/ui/typography'

export const ResearchSubNavbar = () => {

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const { boardId } = useParams()
    const { getAllAdvertisersFromState, getAllBoardsFromState, updateManyBoardsState } = useResearchContext()
    const advertisers = getAllAdvertisersFromState()
    const boards = getAllBoardsFromState()

    // State for the new board dialog
    const [isNewBoardDialogOpen, setIsNewBoardDialogOpen] = useState(false)
    const [newBoardName, setNewBoardName] = useState('')
    const goToBrandPage = (advertiser) => {
        navigate(`research/${advertiser._id}`)
        AnalyticsService.track('navigate-to-brand-page-from-nav-bar', advertiser)
    }

    const goToAllBrands = () => {
        navigate('research')
        AnalyticsService.track('navigate-to-all-brands')
    }

    const goToDiscovery = () => {
        navigate('research/discovery')
        AnalyticsService.track('navigate-to-discovery-page')
    }

    const createNewBoard = () => {
        setIsNewBoardDialogOpen(true)
        AnalyticsService.track('open-create-new-board-dialog')
    }

    const handleDialogClose = () => {
        setIsNewBoardDialogOpen(false)
        setNewBoardName('')
    }

    const handleCreateBoard = async () => {
        try {
            const trimmedBoardName = newBoardName.trim()
            if (!trimmedBoardName) {
                return
            }
            handleDialogClose()
            toggleLoader(true)
            const newBoardResponse = await API.createBoard({ name: trimmedBoardName })
            const newBoard = newBoardResponse.data
            updateManyBoardsState([newBoard])
            toast.success('Board created successfully')

            AnalyticsService.track('create-new-board', { boardName: trimmedBoardName })

        } catch (e) {
            toast.error(e.message)
            console.log(e)
            handleError(e)
        }
        finally {
            toggleLoader(false)
        }
    }


    return (
        <div className="pt-4">

            <div className="space-y-2">
                {/* Discovery Option */}
                {/* <Button
                    variant="ghost"
                    className="w-full justify-start gap-2 px-6 py-2 mt-4"
                    onClick={goToDiscovery}
                >
                    <Icon icon="ix:explore" className="h-4 w-4" />
                    <span>Discovery</span>
                </Button> */}

                {/* My Brands Accordion */}
                <Accordion
                    type="single"
                    collapsible
                    defaultValue={'my-brands'}
                    className="w-full px-2 font-light"
                >
                    <AccordionItem value="my-brands" className="border-0">
                        <AccordionTrigger className="px-4">
                            <div className="flex items-center gap-2">
                                <Icon icon="tabler:brand-itch" className="h-4 w-4" />
                                <span>My Brands</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div
                                className="pl-2 space-y-1 border-0 overflow-scroll max-h-[60vh] p-4"
                            >
                                {/* All Brands option */}
                                <motion.div
                                    className={`flex items-center gap-2 relative transition-colors hover:bg-gray-200 cursor-pointer p-2 rounded-md ${pathname.endsWith('/research') ? 'bg-gray-200' : ''}`}
                                    initial={{ opacity: 0, left: '-30px' }}
                                    animate={{ opacity: 1, left: 0 }}
                                    transition={{ type: "spring", bounce: 0.2 }}
                                    onClick={goToAllBrands}
                                >
                                    <div className="flex items-center justify-center rounded-full w-8 h-8 bg-primary/10">
                                        <Icon icon="mdi:view-grid-outline" className="h-4 w-4 text-primary" />
                                    </div>
                                    <span className="font-medium">All Brands</span>
                                </motion.div>

                                {/* Divider */}
                                <div className="h-px bg-gray-200 my-2"></div>

                                {/* Individual brands */}
                                {
                                    advertisers.map((advertiser, index) => {
                                        const isChosen = pathname.includes(advertiser._id)
                                        return (
                                            <motion.div
                                                key={index}
                                                className={`flex items-center gap-2 relative transition-colors hover:bg-gray-200 cursor-pointer p-2 rounded-md ${isChosen ? 'bg-gray-200' : ''}`}
                                                initial={{ opacity: 0, left: '-30px' }}
                                                animate={{ opacity: 1, left: 0 }}
                                                transition={{ type: "spring", bounce: 0.2, delay: 0.03 * index }}
                                                onClick={() => goToBrandPage(advertiser)}
                                            >
                                                <img src={advertiser.profilePictureUrl} className="rounded-full w-8 h-8" alt="" />
                                                <span className="truncate">{advertiser.title}</span>
                                            </motion.div>
                                        )
                                    })
                                }
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>

                {/* My Boards Accordion */}
                <Accordion
                    type="single"
                    collapsible
                    defaultValue={'my-boards'}
                    className="w-full px-2 font-light"
                >
                    <AccordionItem value="my-boards" className="border-0">
                        <AccordionTrigger className="px-4">
                            <div className="flex items-center gap-2">
                                <Icon icon="fluent:board-16-regular" className="h-4 w-4" />
                                <span>My Boards</span>
                            </div>
                        </AccordionTrigger>
                        <AccordionContent>
                            <div className="pl-2 space-y-2 border-0 p-4">
                                <Button
                                    variant="primary-outline"
                                    className="w-full gap-2"
                                    size="sm"
                                    onClick={createNewBoard}
                                >
                                    <Icon icon="icons8:plus" className="h-4 w-4" />
                                    Create New
                                </Button>

                                {/* Divider */}
                                <div className="h-px bg-gray-200 my-2"></div>
                                {
                                    boards.map((board, index) => {
                                        const isSelected = board._id === boardId
                                        return (
                                            <motion.div
                                                key={board._id}
                                                className={`flex items-center px-3 py-2 text-sm relative hover:bg-accent cursor-pointer rounded-md ${isSelected ? 'bg-accent' : ''}`}
                                                initial={{ opacity: 0, left: '-30px' }}
                                                animate={{ opacity: 1, left: 0 }}
                                                transition={{ type: "spring", bounce: 0.2, delay: 0.03 * index }}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    navigate(`research/board/${board._id}`)
                                                }}
                                            >
                                                <div className="flex items-center justify-center rounded-full w-8 h-8 bg-primary/10 mr-2">
                                                    <Icon icon="fluent:board-16-regular" className="h-4 w-4 text-primary" />
                                                </div>
                                                <span className="truncate">{board.name}</span>
                                            </motion.div>
                                        )
                                    })
                                }
                                {
                                    boards.length === 0 && (
                                        <TypographySmallMuted className="text-center mt-2">
                                            - No boards created yet -
                                        </TypographySmallMuted>
                                    )
                                }
                            </div>
                        </AccordionContent>
                    </AccordionItem>
                </Accordion>
            </div>

            {/* New Board Dialog */}
            <Dialog open={isNewBoardDialogOpen} onOpenChange={setIsNewBoardDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Board</DialogTitle>
                    </DialogHeader>

                    <div className="py-4">
                        <Input
                            placeholder="Board name"
                            value={newBoardName}
                            onChange={(e) => setNewBoardName(e.target.value)}
                            className="w-full"
                            autoFocus
                        />
                    </div>

                    <DialogFooter className="flex justify-between sm:justify-between">
                        <Button
                            variant="outline"
                            onClick={handleDialogClose}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleCreateBoard}
                            disabled={!newBoardName.trim()}
                        >
                            Create
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}